import React from 'react';
import { MdNavigateBefore, MdNavigateNext } from 'react-icons/md';
import { useTranslation } from "react-i18next";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  maxVisiblePages?: number;
  className?: string;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  maxVisiblePages = 5,
  className = ''
}) => {
  const { t } = useTranslation();
  if (totalPages <= 1) return null;

  const pageButtons: React.ReactNode[] = [];

  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

  // Adjust if we're near the end
  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  // Prev arrow
  pageButtons.push(
    <button
      key="prev"
      onClick={() => onPageChange(Math.max(1, currentPage - 1))}
      disabled={currentPage === 1}
      className={`mx-1 px-3 py-2 rounded-md ${
        currentPage === 1
          ? 'text-gray-400 cursor-not-allowed'
          : 'text-gray-700 hover:bg-gray-200'
      }`}
      aria-label={t("pagination.previous")}
    >
      <MdNavigateBefore />
    </button>
  );

  // Page numbers
  for (let i = startPage; i <= endPage; i++) {
    pageButtons.push(
      <button
        key={i}
        onClick={() => onPageChange(i)}
        className={`mx-1 px-3 py-1 rounded-md ${
          currentPage === i
            ? 'bg-orange-500 text-white'
            : 'text-gray-700 hover:bg-gray-200'
        }`}
        aria-current={currentPage === i ? 'page' : undefined}
      >
        {i}
      </button>
    );
  }

  // Next arrow
  pageButtons.push(
    <button
      key="next"
      onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
      disabled={currentPage === totalPages}
      className={`mx-1 px-3 py-2 rounded-md ${
        currentPage === totalPages
          ? 'text-gray-400 cursor-not-allowed'
          : 'text-gray-700 hover:bg-gray-200'
      }`}
      aria-label={t("pagination.next")}
    >
      <MdNavigateNext />
    </button>
  );

  return (
    <div className={`flex justify-center items-center mt-8 ${className}`}>{pageButtons}</div>
  );
};

export default Pagination;
